import { createSelector } from "reselect";
import type { Attribute, FieldValue } from "../../types";
import { getAllAttributes } from "../../utils/field-utils";
import { getAttributeOptions, isOptionsEmpty } from "../../utils/option-utils";
import type { FormStore } from "../types";

/**
 * 属性値を取得する
 */
const getAttributes = createSelector(
  [(state: FormStore) => state.form],
  (form) => {
    if (!form || !form.form) {
      return [];
    }
    return getAllAttributes(form);
  },
);

// 表示対象の属性を返す共通セレクター
const getVisibleAttributes = createSelector([getAttributes], (attributes) =>
  attributes.filter(
    (attr) =>
      !attr.id.endsWith("PreviousValue") &&
      attr.type !== "Hidden" &&
      !isSelectFieldWithoutOptions(attr),
  ),
);

/**
 * 選択肢を持つフィールドで選択肢が空かどうかを判定する
 * @param attribute 属性
 * @returns 選択肢が空の場合true
 */
const isSelectFieldWithoutOptions = (attribute: Attribute): boolean => {
  const optionBasedTypes: Attribute["type"][] = [
    "SingleSelect",
    "MultiSelectCheckbox",
    "Radio",
    "ChangeSingleSelect",
    "ChangeRadio",
  ];
  if (!optionBasedTypes.includes(attribute.type)) {
    return false;
  }
  const options = getAttributeOptions(attribute);
  return isOptionsEmpty(options);
};

/**
 * フィールドが入力済みかどうかを判定する
 * @param value フィールドの値
 * @param attributeId 属性ID（Previous値の判定に使用）
 * @param attribute 属性定義
 * @returns 入力済みの場合true
 */
const isFieldFilled = (
  value: unknown,
  attributeId?: string,
  attribute?: Attribute,
): boolean => {
  // undefined, null は未入力
  if (value === undefined || value === null) {
    return false;
  }

  // Previous値（変更前の値）は進捗計算に含めない
  if (attributeId && attributeId.endsWith("PreviousValue")) {
    return false;
  }

  // Hiddenフィールドは進捗計算に含めない
  if (attribute?.type === "Hidden") {
    return false;
  }

  // 選択肢のないフィールドは進捗計算に含めない
  if (attribute && isSelectFieldWithoutOptions(attribute)) {
    return false;
  }

  // 文字列の場合、空文字は未入力
  if (typeof value === "string") {
    return value !== "";
  }

  // 配列の場合、空配列は未入力
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

/**
 * フォーム全体のフィールド（Attribute）の総数を取得する
 */
export const getTotalFieldCount = createSelector(
  [getVisibleAttributes],
  (visibleAttributes) => visibleAttributes.length,
);

/**
 * フォーム全体のフィールド（Attribute）の必須（Required）の総数を取得する
 */
export const getRequiredFieldCount = createSelector(
  [getVisibleAttributes],
  (visibleAttributes) =>
    visibleAttributes.filter((attr) => attr.required).length,
);

/**
 * フォーム全体のフィールド（Attribute）の入力済数を取得する
 * 適切な値が入力されているフィールドを入力済みとみなす
 */
export const getFilledFieldCount = createSelector(
  [(state: FormStore) => state.values, (state: FormStore) => state.form],
  (values, form) => {
    if (!form || !form.form) {
      return 0;
    }
    const allAttributes = getAllAttributes(form);
    const attributeMap = new Map(allAttributes.map((attr) => [attr.id, attr]));

    return values.filter((field) => {
      const attr = attributeMap.get(field.attributeId);
      return isFieldFilled(field.value, field.attributeId, attr);
    }).length;
  },
);

/**
 * フォーム全体のフィールド（Attribute）の必須（Required）の入力済数を取得する
 */
export const getFilledRequiredFieldCount = createSelector(
  [(state: FormStore) => state.values, (state: FormStore) => state.form],
  (values, form) => {
    if (!form || !form.form) {
      return 0;
    }

    const allAttributes = getAllAttributes(form);
    const attributeMap = new Map(allAttributes.map((attr) => [attr.id, attr]));

    // 必須フィールドIDとその値のマッピングを作成
    return values.filter((field: FieldValue) => {
      const attr = attributeMap.get(field.attributeId);

      // フィールドが入力済みかチェック
      if (!isFieldFilled(field.value, field.attributeId, attr)) {
        return false;
      }

      // 必須フィールドかチェック
      return attr?.required ?? false;
    }).length;
  },
);
